/**
 * ============================================================================
 * WorkbenchStats — Phase 6e frontend (Equipment & Validation Repository).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: hero KPI strip at the top of the Equipment Workbench. Four oversized
 *          tiles: Total Qualified, Restricted Count, Cal Due This Week, Requal
 *          Due This Month.
 *
 * VISUAL: editorial style — large numeral, quiet label, no spurious deltas.
 *  Mirrors the SupplierWorkbench WorkbenchStats look (14 px radius, 1 px
 *  border, subtle shadow). Tone is set per-tile via a band dot.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './EquipmentWorkbench.module.css';
import type { EquipmentWorkbenchStats } from './useEquipmentQuery';

export interface WorkbenchStatsProps {
  stats: EquipmentWorkbenchStats | undefined;
  isLoading: boolean;
}

type Tone = 'neutral' | 'critical' | 'warn' | 'info';

interface Tile {
  key: keyof EquipmentWorkbenchStats;
  labelKey: string;
  defaultLabel: string;
  tone: Tone;
}

const TILES: Tile[] = [
  {
    key: 'total_qualified',
    labelKey: 'equipment.workbench.stats.totalQualified',
    defaultLabel: 'Total qualified',
    tone: 'neutral',
  },
  {
    key: 'restricted_count',
    labelKey: 'equipment.workbench.stats.restrictedCount',
    defaultLabel: 'Restricted count',
    tone: 'warn',
  },
  {
    key: 'cal_due_this_week',
    labelKey: 'equipment.workbench.stats.calDueWeek',
    defaultLabel: 'Cal due this week',
    tone: 'critical',
  },
  {
    key: 'requal_due_this_month',
    labelKey: 'equipment.workbench.stats.requalDueMonth',
    defaultLabel: 'Requal due this month',
    tone: 'info',
  },
];

const TONE_DOT: Record<Tone, string> = {
  neutral: styles.dotNeutral,
  critical: styles.dotCritical,
  warn: styles.dotWarn,
  info: styles.dotInfo,
};

export function WorkbenchStats({ stats, isLoading }: WorkbenchStatsProps) {
  const { t, i18n } = useTranslation();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  if (isLoading) {
    return (
      <section
        className={styles.statsRow}
        aria-label={t('equipment.workbench.stats.regionAria', {
          defaultValue: 'Hero KPIs',
        })}
      >
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={styles.statSkeleton} aria-hidden="true" />
        ))}
      </section>
    );
  }

  return (
    <section
      className={styles.statsRow}
      aria-label={t('equipment.workbench.stats.regionAria', {
        defaultValue: 'Hero KPIs',
      })}
    >
      {TILES.map((tile) => {
        const value = stats ? stats[tile.key] : 0;
        const label = t(tile.labelKey, { defaultValue: tile.defaultLabel });
        return (
          <article
            key={tile.key}
            className={styles.statTile}
            aria-label={t('equipment.workbench.stats.tileAria', {
              defaultValue: '{{label}}: {{value}}',
              label,
              value: nf.format(value),
            })}
          >
            <header className={styles.statTileHeader}>
              <span className={styles.statTileLabel}>{label}</span>
              <span
                className={`${styles.statDot} ${TONE_DOT[tile.tone]}`}
                aria-hidden="true"
              />
            </header>
            <div className={styles.statTileValue}>{nf.format(value)}</div>
          </article>
        );
      })}
    </section>
  );
}
